import React from 'react';
import { MembershipItem, MEMBERSHIP_ITEM_TYPES, MembershipItemTypeValue } from '@spike/membership-model';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import Service from 'model/Service';
import {
    useDiscountedItemSelection,
    UseDiscountedItemSelectionReturn,
    DiscountMap
} from 'hooks/useDiscountedItemSelection';
import { toMembershipItem } from '../utils/membershipItemUtils';
import { useMembershipItemCatalogs } from './useMembershipItemCatalogs';

export type { DiscountMap };

interface UseMembershipDiscountedItemDrawersConfig {
    items: MembershipItem[];
    excludedItemIDs: string[];
    marketplaceID: number;
    onChange: (items: MembershipItem[]) => void;
}

export interface DiscountedDrawerState<T> {
    drawer: UseDiscountedItemSelectionReturn<T>;
    availableItems: T[];
    currentItems: T[];
    catalogItems: T[];
    existingDiscounts: DiscountMap;
}

export interface UseMembershipDiscountedItemDrawersReturn {
    openDrawerForType: (type: MembershipItemTypeValue) => void;
    /** Opens the correct drawer for an item, resolving legacy item types automatically */
    resolveAndOpenDrawer: (item: MembershipItem) => void;
    grooming: DiscountedDrawerState<Service>;
    daycare: DiscountedDrawerState<Service>;
    boarding: DiscountedDrawerState<Service>;
    walking: DiscountedDrawerState<Service>;
    transportation: DiscountedDrawerState<Service>;
    training: DiscountedDrawerState<Service>;
    addons: DiscountedDrawerState<AddOnService>;
    products: DiscountedDrawerState<Product>;
}

const nameFilter = (item: { name: string }, query: string) => item.name.toLowerCase().includes(query.toLowerCase());

/** Extracts a DiscountMap from normalizedItems for a given itemType */
const buildExistingDiscounts = (normalizedItems: MembershipItem[], itemType: MembershipItemTypeValue): DiscountMap => {
    const map: DiscountMap = {};
    normalizedItems
        .filter(i => i.itemType === itemType)
        .forEach(i => {
            const hasPercentage =
                i.discountPercentage !== null &&
                i.discountPercentage !== undefined &&
                (i.discountPercentage as number) > 0;
            map[String(i.itemID)] = {
                value: hasPercentage ? (i.discountPercentage as number) : i.discountAmount || 0,
                type: hasPercentage ? 'percentage' : 'amount'
            };
        });
    return map;
};

export const useMembershipDiscountedItemDrawers = ({
    items,
    excludedItemIDs,
    marketplaceID,
    onChange
}: UseMembershipDiscountedItemDrawersConfig): UseMembershipDiscountedItemDrawersReturn => {
    const {
        groomingCatalog,
        daycareCatalog,
        boardingCatalog,
        walkingCatalog,
        transportationCatalog,
        trainingCatalog,
        addonsCatalog,
        productsCatalog,
        availableGrooming,
        availableDaycare,
        availableBoarding,
        availableWalking,
        availableTransportation,
        availableTraining,
        availableAddons,
        availableProducts,
        currentGrooming,
        currentDaycare,
        currentBoarding,
        currentWalking,
        currentTransportation,
        currentTraining,
        currentAddons,
        currentProducts,
        normalizedItems
    } = useMembershipItemCatalogs({ items, excludedItemIDs });

    // ─── Existing discounts per category ──────────────────────────────────────

    const existingGroomingDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE),
        [normalizedItems]
    );
    const existingDaycareDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE),
        [normalizedItems]
    );
    const existingBoardingDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE),
        [normalizedItems]
    );
    const existingWalkingDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE),
        [normalizedItems]
    );
    const existingTransportationDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE),
        [normalizedItems]
    );
    const existingTrainingDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE),
        [normalizedItems]
    );
    const existingAddonsDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.ADD_ON),
        [normalizedItems]
    );
    const existingProductsDiscounts = React.useMemo(
        () => buildExistingDiscounts(normalizedItems, MEMBERSHIP_ITEM_TYPES.PRODUCT),
        [normalizedItems]
    );

    // ─── onChange factory ─────────────────────────────────────────────────────

    const itemsRef = React.useRef(normalizedItems);
    itemsRef.current = normalizedItems;

    const onChangeRef = React.useRef(onChange);
    onChangeRef.current = onChange;

    const makeOnChange = React.useCallback(
        (itemType: MembershipItemTypeValue) =>
            (selected: Array<Service | AddOnService | Product>, discounts: DiscountMap) => {
                const newItems = selected.map(s => {
                    const base = toMembershipItem(s, itemType, marketplaceID, true);
                    const d = discounts[String(s.id)];
                    return {
                        ...base,
                        discountAmount: d?.type === 'amount' ? d.value : null,
                        discountPercentage: d?.type === 'percentage' ? d.value : null
                    };
                });
                const otherItems = itemsRef.current.filter(i => i.itemType !== itemType);
                onChangeRef.current([...otherItems, ...newItems]);
            },
        [marketplaceID]
    );

    // ─── useDiscountedItemSelection instances ─────────────────────────────────

    const groomingDrawer = useDiscountedItemSelection<Service>({
        items: availableGrooming,
        selectedItems: currentGrooming,
        existingDiscounts: existingGroomingDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE),
        filterFn: nameFilter
    });

    const daycareDrawer = useDiscountedItemSelection<Service>({
        items: availableDaycare,
        selectedItems: currentDaycare,
        existingDiscounts: existingDaycareDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE),
        filterFn: nameFilter
    });

    const boardingDrawer = useDiscountedItemSelection<Service>({
        items: availableBoarding,
        selectedItems: currentBoarding,
        existingDiscounts: existingBoardingDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE),
        filterFn: nameFilter
    });

    const walkingDrawer = useDiscountedItemSelection<Service>({
        items: availableWalking,
        selectedItems: currentWalking,
        existingDiscounts: existingWalkingDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE),
        filterFn: nameFilter
    });

    const transportationDrawer = useDiscountedItemSelection<Service>({
        items: availableTransportation,
        selectedItems: currentTransportation,
        existingDiscounts: existingTransportationDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE),
        filterFn: nameFilter
    });

    const trainingDrawer = useDiscountedItemSelection<Service>({
        items: availableTraining,
        selectedItems: currentTraining,
        existingDiscounts: existingTrainingDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE),
        filterFn: nameFilter
    });

    const addonsDrawer = useDiscountedItemSelection<AddOnService>({
        items: availableAddons,
        selectedItems: currentAddons,
        existingDiscounts: existingAddonsDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.ADD_ON),
        filterFn: nameFilter
    });

    const productsDrawer = useDiscountedItemSelection<Product>({
        items: availableProducts,
        selectedItems: currentProducts,
        existingDiscounts: existingProductsDiscounts,
        onSelectedItemsChange: makeOnChange(MEMBERSHIP_ITEM_TYPES.PRODUCT),
        filterFn: nameFilter
    });

    // ─── openDrawerForType dispatcher ─────────────────────────────────────────

    const openDrawerForType = React.useCallback(
        (type: MembershipItemTypeValue) => {
            switch (type) {
                case MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE:
                    groomingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE:
                    daycareDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE:
                    boardingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE:
                    walkingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE:
                    transportationDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE:
                    trainingDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.ADD_ON:
                    addonsDrawer.openDrawer();
                    break;
                case MEMBERSHIP_ITEM_TYPES.PRODUCT:
                    productsDrawer.openDrawer();
                    break;
            }
        },
        [
            groomingDrawer,
            daycareDrawer,
            boardingDrawer,
            walkingDrawer,
            transportationDrawer,
            trainingDrawer,
            addonsDrawer,
            productsDrawer
        ]
    );

    // ─── resolveAndOpenDrawer: handles legacy "Primary Service" types ──────────

    const normalizedItemsRef = React.useRef(normalizedItems);
    normalizedItemsRef.current = normalizedItems;

    const resolveAndOpenDrawer = React.useCallback(
        (item: MembershipItem) => {
            const normalized = normalizedItemsRef.current.find(ni => ni.uuid === item.uuid);
            if (normalized) {
                openDrawerForType(normalized.itemType as MembershipItemTypeValue);
            }
        },
        [openDrawerForType]
    );

    return {
        openDrawerForType,
        resolveAndOpenDrawer,
        grooming: {
            drawer: groomingDrawer,
            availableItems: availableGrooming,
            currentItems: currentGrooming,
            catalogItems: groomingCatalog,
            existingDiscounts: existingGroomingDiscounts
        },
        daycare: {
            drawer: daycareDrawer,
            availableItems: availableDaycare,
            currentItems: currentDaycare,
            catalogItems: daycareCatalog,
            existingDiscounts: existingDaycareDiscounts
        },
        boarding: {
            drawer: boardingDrawer,
            availableItems: availableBoarding,
            currentItems: currentBoarding,
            catalogItems: boardingCatalog,
            existingDiscounts: existingBoardingDiscounts
        },
        walking: {
            drawer: walkingDrawer,
            availableItems: availableWalking,
            currentItems: currentWalking,
            catalogItems: walkingCatalog,
            existingDiscounts: existingWalkingDiscounts
        },
        transportation: {
            drawer: transportationDrawer,
            availableItems: availableTransportation,
            currentItems: currentTransportation,
            catalogItems: transportationCatalog,
            existingDiscounts: existingTransportationDiscounts
        },
        training: {
            drawer: trainingDrawer,
            availableItems: availableTraining,
            currentItems: currentTraining,
            catalogItems: trainingCatalog,
            existingDiscounts: existingTrainingDiscounts
        },
        addons: {
            drawer: addonsDrawer,
            availableItems: availableAddons,
            currentItems: currentAddons,
            catalogItems: addonsCatalog,
            existingDiscounts: existingAddonsDiscounts
        },
        products: {
            drawer: productsDrawer,
            availableItems: availableProducts,
            currentItems: currentProducts,
            catalogItems: productsCatalog,
            existingDiscounts: existingProductsDiscounts
        }
    };
};
